import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  IconButton,
  Box,
  Tooltip,
} from "@avenue-8/sidekick-ui";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COMPARABLE_SORT_OPTIONS_ARRAY } from "../../../shared/helpers/property-search/comparable-sort-options";
import styled from "@emotion/styled";

const SortByButton = styled(Button)`
  max-width: auto;
  padding: 0 0.75rem;
  height: 36px;
  font-size: ${(p) => p.theme.text.fontSizes.xs};
`;

const SortDirectionButton = styled(IconButton)`
  height: 36px;
  width: 36px;
  svg {
    font-size: ${(p) => p.theme.text.fontSizes.l};
  }
`;

interface SortByProps {
  value?: {
    criteria: string;
    asc: boolean;
  };
  onChange: (sortBy: { criteria: string; asc: boolean }) => void;
  disabled?: boolean;
}

export const SortBy: React.FC<SortByProps> = ({
  value = { criteria: "listdate", asc: false },
  onChange,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCriteriaChange = (criteria: string) => {
    const sortOption = COMPARABLE_SORT_OPTIONS_ARRAY.find(
      (option) => option.key === criteria
    );

    const initialAsc = sortOption?.initialDirection === "asc" || false;

    onChange({
      criteria,
      asc: initialAsc,
    });
    handleClose();
  };

  const handleDirectionToggle = () => {
    const currentOption = COMPARABLE_SORT_OPTIONS_ARRAY.find(
      (option) => option.key === value.criteria
    );

    if (currentOption?.initialDirection !== null) {
      onChange({
        ...value,
        asc: !value.asc,
      });
    }
  };

  const currentOption = COMPARABLE_SORT_OPTIONS_ARRAY.find(
    (option) => option.key === value.criteria
  );
  const canReverse = currentOption?.initialDirection !== null;

  return (
    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
      <SortByButton
        onClick={handleClick}
        disabled={disabled}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transition: "300ms",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        }
        variant="text"
        size="small"
      >
        {currentOption?.label || "Sort"}
      </SortByButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {COMPARABLE_SORT_OPTIONS_ARRAY.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => handleCriteriaChange(option.key)}
            selected={value.criteria === option.key}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Tooltip
        title={
          !canReverse
            ? "Cannot reverse sort"
            : value.asc
              ? "Sort Descending"
              : "Sort Ascending"
        }
      >
        <span>
          <SortDirectionButton
            onClick={handleDirectionToggle}
            disabled={disabled || !canReverse}
          >
            {value.asc ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            )}
          </SortDirectionButton>
        </span>
      </Tooltip>
    </Box>
  );
};
